import Vue from 'vue'
import {ProductApi, SubstanceApi} from "@core/api";
import {DocumentType, ProductType, VerificationDocumentType} from "@/libs/product";
import {getProductPropertyDefaultValue} from "@core/domain/substance";
import {ProductPropertyType, SubstanceListClass} from "@core/domain/substance";

export default class Product {
    id;
    inited = false;
    isSafeUseApplicable = Vue.observable(null);
    card = {
        hasConcernSubstances: null,
        countryOfOrigin: null,
        identifiers: [],
        names: [],
        businessRoles: [],
        comment: '',
        includedProducts: [],
        productGroups: [],
        categories: [],
        materialCategoryIds: [],
        mixtureCategoryIds: [],
        suppliers: [],
        characteristics: {},
        informationOwnerSettings: {
            weHaveInformation: null,
            supplierHasInformation: null
        },
        legislations: {},
        substanceListQuestionStates: [],
        regulatedProductStatements: [],
        legislationQuestionAnswers: [],
    };
    scipNotificationFacts = null;
    hierarchy = null;
    cardPromise;
    suppliers = [];
    concernElements = [];

    children = [];

    includedSubstances = Vue.observable([]);
    excludedSubstances = Vue.observable([]);
    componentsWithConcernElements = [];
    legislationQuestionAnswers = Vue.observable([]);
    legislationQuestionAnswersPromise;

    substancesInited = false;
    documentesInited = false;

    data = {};
    descriptionDocument = Vue.observable({
        file: null
    });
    documents = [];
    declarationOfConformityDocuments = [];
    compliantStatementDocuments = [];
    evidenceReportDocuments = [];
    otherVerificationDocuments = [];
    safeUseDocument = Vue.observable({
        description: '',
        file: null
    });
    dissemblingDocument = Vue.observable({
        description: '',
        file: null
    });

    supplierVerificationDocumentsInited = false;
    supplierVerificationDocumentsDirty = false;
    supplierVerificationDocuments = Vue.observable({
        declarationsOfConformity: [],
        compliantStatement: [],
        evidenceReports: [],
        other: [],
        safeUseInstructions: {
            description: '',
            file: null,
        },
        dissemblingInstructions: {
            description: '',
            file: null,
        },
        description: null,
        unclassifiedVerification: [],
    });
    supplierVerificationReplyDocuments = Vue.observable({
        requestId: null,
        declarationsOfConformity: [],
        compliantStatement: [],
        evidenceReports: [],
        safeUseInstruction: null,
        dissemblingInstruction: null,
    });

    fmdInitialized = false;
    fmd = Vue.observable({
        productId: null,
        version: 1,
        data: {
            declarations: []
        },
        page: {
            cursor: null,
            nextCursor: null,
            hasMore: null
        }
    })

    /**
     *
     * @type {SubstanceListClass[]}
     */
    substanceLists = [];

    constructor(id, substanceListPromise) {
        this.id = id;
        this.fmd.productId = id;
        substanceListPromise.then(lists => {
            this.substanceLists = lists;
        });
    }

    //  Getters
    isInited() {
        return this.inited;
    }

    getState() {
        const state = {
            attributes: this.__gatherProductSaveAttributes(),
            includedSubstances: this.__gatherIncludedSubstances(),
            excludedSubstances: this.__gatherExcludedSubstances(),
            documents: {
                doc: this.declarationOfConformityDocuments.map(d => {
                    return {
                        id: d.id,
                        declarationDate: d.declarationDate,
                        declarationValidityDate: d.declarationValidityDate,
                        file: d.file,
                        legislations: this.__gatherIdList(d.legislations)
                    };
                }),
                compliantStatement: this.compliantStatementDocuments.map(d => {
                    return {
                        id: d.id,
                        declarationDate: d.declarationDate,
                        declarationValidityDate: d.declarationValidityDate,
                        file: d.file,
                        legislations: this.__gatherIdList(d.legislations)
                    };
                }),
                evidence: this.evidenceReportDocuments.map(d => {
                    return {
                        id: d.id,
                        documentTypes: this.__gatherIdList(d.documentTypes),
                        file: d.file,
                        legislations: this.__gatherIdList(d.legislations)
                    };
                }),
                safeUse: {
                    description: this.safeUseDocument.description,
                    id: this.safeUseDocument.id,
                    file: this.safeUseDocument.file,
                },
                dissembling: {
                    description: this.dissemblingDocument.description,
                    id: this.dissemblingDocument.id,
                    file: this.dissemblingDocument.file,
                }
            },
            supplierVerificationDocuments: this.__gatherSupplierVerificationDocumentsState(),
        };
        state.attributes.names = state.attributes.names.map(n => {
            return {key: n.key, value: n.value};
        });

        state.attributes.identifiers = state.attributes.identifiers.map(n => {
            return {key: n.key, value: n.value};
        });

        return state;
    }

    getDocuments(refresh = false) {
        if (refresh) {
            this.__cleanCache(['documents']);
        }

        return this.__get('documents', (resolve, reject) => {
            ProductApi
                .getOrganizationDocuments(this.id)
                .then(r => {
                    this.documentesInited = true;
                    this.documents = r.data;
                    const verificationDocuments = this.documents.filter(d => d.documentType === DocumentType.VERIFICATION);

                    this.evidenceReportDocuments = Vue.observable(verificationDocuments.filter(d => d.verificationType === VerificationDocumentType.EVIDENCE_REPORT));
                    this.declarationOfConformityDocuments = Vue.observable(verificationDocuments.filter(d => d.verificationType === VerificationDocumentType.DECLARATION_OR_CONFORMITY));
                    this.compliantStatementDocuments = Vue.observable(verificationDocuments.filter(d => d.verificationType === VerificationDocumentType.COMPLIANT_STATEMENT));
                    this.otherVerificationDocuments = Vue.observable(verificationDocuments.filter(d => d.verificationType === VerificationDocumentType.OTHER));
                    const safeUse = verificationDocuments.find(d => d.verificationType === VerificationDocumentType.SAFE_USE_INSTRUCTIONS);
                    const dissembling = verificationDocuments.find(d => d.verificationType === VerificationDocumentType.DISSEMBLING_INSTRUCTIONS);

                    Vue.set(this.safeUseDocument, 'id', safeUse ? safeUse.id : null);
                    this.safeUseDocument.file = safeUse && safeUse.file ? safeUse.file : null;
                    this.safeUseDocument.description = safeUse && safeUse.description ? safeUse.description : '';
                    Vue.set(this.safeUseDocument, 'document', null);

                    Vue.set(this.dissemblingDocument, 'id', dissembling ? dissembling.id : null);
                    this.dissemblingDocument.file = dissembling && dissembling.file ? dissembling.file : null;
                    this.dissemblingDocument.description = dissembling && dissembling.description ? dissembling.description : '';
                    Vue.set(this.dissemblingDocument, 'document', null);

                    const descriptionDocument = this.documents.find(d => {
                        return d.documentType === DocumentType.DESCRIPTION;
                    });

                    if (descriptionDocument) {
                        Vue.set(this.descriptionDocument, 'id', descriptionDocument.id);
                        this.descriptionDocument.file = descriptionDocument.file || null;
                    } else {
                        Vue.set(this.descriptionDocument, 'id', null);
                        this.descriptionDocument.file = null;
                    }
                    Vue.set(this.descriptionDocument, 'document', null);

                    resolve({
                        declarationOfConformityDocuments: this.declarationOfConformityDocuments,
                        evidenceReportDocuments: this.evidenceReportDocuments,
                        compliantStatementDocuments: this.compliantStatementDocuments,
                        otherVerificationDocuments: this.otherVerificationDocuments,
                        safeUseDocument: this.safeUseDocument,
                        dissemblingDocument: this.dissemblingDocument,
                        productDescription: this.descriptionDocument
                    });
                })
                .catch(reject);
        });
    }

    getChildren(refresh = false) {
        if (refresh) {
            this.__cleanCache(['children']);
        }
        return this.__get('children', (resolve, reject) => {
            ProductApi
                .getChildren(this.id)
                .then(r => {
                    this.children = r.data;
                    resolve(this.children);
                })
                .catch(reject)
            ;
        }, () => {
            this.children = null;
        }).then(children => {
            return children.map(child => {
                return {...child};
            });
        });
    }

    getSupplierDocuments() {
        return ProductApi.getSupplierDocuments(this.id).then(r => r.data);
    }

    getSupplierVerificationDocuments(refresh = false) {
        if (refresh) {
            this.__cleanCache(['supplierVerificationDocuments']);
        }

        return this.__get('supplierVerificationDocuments', (resolve, reject) => {
            ProductApi
                .getSupplierVerificationDocuments(this.id)
                .then(r => {
                    this.__replaceSupplierVerificationDocuments(
                        this.__normalizeSupplierVerificationDocuments(r.data)
                    );
                    this.supplierVerificationDocumentsInited = true;
                    this.supplierVerificationDocumentsDirty = false;
                    resolve(this.supplierVerificationDocuments);
                })
                .catch(reject);
        });
    }

    getSupplierVerificationReplyDocuments(refresh = false) {
        if (refresh) {
            this.__cleanCache(['supplierVerificationReplyDocuments']);
        }

        return this.__get('supplierVerificationReplyDocuments', (resolve, reject) => {
            ProductApi
                .getSupplierVerificationReplyDocuments(this.id)
                .then(r => {
                    this.__replaceSupplierVerificationReplyDocuments(
                        this.__normalizeSupplierVerificationReplyDocuments(r.data)
                    );
                    resolve(this.supplierVerificationReplyDocuments);
                })
                .catch(error => {
                    if (error && error.response && error.response.status === 404) {
                        this.__replaceSupplierVerificationReplyDocuments(
                            this.__makeEmptySupplierVerificationReplyDocuments()
                        );
                        resolve(this.supplierVerificationReplyDocuments);
                        return;
                    }

                    reject(error);
                });
        });
    }

    setSupplierVerificationDocuments(documents) {
        this.__replaceSupplierVerificationDocuments(documents);
        this.supplierVerificationDocumentsDirty = true;
    }

    markSupplierVerificationDocumentsDirty() {
        this.supplierVerificationDocumentsDirty = true;
    }

    getComponentsSubstances(refresh = false) {
        if (refresh) {
            this.__cleanCache(['componentsConcernElements']);
        }
        return this.__get('componentsConcernElements', (resolve, reject) => {
            ProductApi
                .componentsSubstances(this.id)
                .then(r => {
                    this.componentsWithConcernElements = Vue.observable(r.data);
                    resolve(this.componentsWithConcernElements);
                })
                .catch(reject)
            ;
        });
    }

    getComponentsSubstancesForLegislations(legislations) {
        return ProductApi
                .componentsSubstances(this.id, legislations)
                .then(r => {
                    return r.data;
                })
            ;
    }

    getHierarchy() {
        return this.__get('hierarchy', (resolve, reject) => {
            this.__log('Fetching hierarchy');
            ProductApi
                .getHierarchy(this.id)
                .then(r => {
                    this.hierarchy = Vue.observable(r.data);
                    resolve(this.hierarchy);
                })
                .catch(reject);
        });
    }

    __fmdDeclarationsPromise;
    getFMDDeclarations(refresh = false) {
        if (this.__fmdDeclarationsPromise && !refresh) {
            return this.__fmdDeclarationsPromise;
        }

        return this.__fmdDeclarationsPromise = ProductApi
            .getFMD(this.id)
            .then(r => {
                this.fmdInitialized = true;
                this.fmd = r.data;
                return this.fmd.data.declarations;
            });
    }

    __substancePromise;

    getSubstances(refresh = false) {
        // return this.__get('substances', (resolve, reject) => {
        if (this.__substancePromise && !refresh) {
            return this.__substancePromise;
        }

        return this.__substancePromise = ProductApi
                .getSubstances(this.id)
                .then(r => {
                    this.substancesInited = true;
                    this.includedSubstances.splice(0);
                    this.excludedSubstances.splice(0);
                    //  {status, substance, details}
                    r.data.map(item => {
                        if (item.status === 'included') {
                            this.includedSubstances.push(item);
                        } else if (item.status === 'excluded') {
                            this.excludedSubstances.push(item)
                        }
                    });

                    return {
                        included: this.includedSubstances,
                        excluded: this.excludedSubstances,
                    };
                })
        // });
    }

    getConcernElements() {
        return this.__get('concern-elements', (resolve, reject) => {
            console.log('[Product]: Fetch concern elements');
            ProductApi
                .getConcernElements(this.id)
                .then(r => {
                    this.concernElements = Vue.observable(r.data);

                    resolve(this.concernElements);
                })
                .catch(reject);
        });
    }

    getCard() {
        if (this.cardPromise) {
            return this.cardPromise;
        }

        return this.cardPromise = new Promise((resolve, reject) => {
            ProductApi
                .getProductCard(this.id)
                .then((response) => {
                    this.inited = true;
                    this.card = Vue.observable(response.data);
                    this.isSafeUseApplicable = this.card.isSafeUseApplicable;
                    resolve(this.card);
                })
                .catch(reject);
        });
    }

    getLegislationQuestionAnswers(refresh = false) {
        if (this.legislationQuestionAnswersPromise && !refresh) {
            return this.legislationQuestionAnswersPromise;
        }

        this.legislationQuestionAnswersPromise = ProductApi
            .getLegislationQuestionAnswers(this.id)
            .then(response => {
                const answers = Array.isArray(response.data.answers) ? response.data.answers : [];

                this.legislationQuestionAnswers.splice(0);
                answers.forEach(answer => this.legislationQuestionAnswers.push(answer));

                return this.legislationQuestionAnswers;
            });

        return this.legislationQuestionAnswersPromise;
    }

    refreshCard() {
        return this.cardPromise = new Promise((resolve, reject) => {
            ProductApi
                .getProductCard(this.id)
                .then((response) => {
                    this.card = response.data;
                    this.isSafeUseApplicable = this.card.isSafeUseApplicable;
                    resolve(this.card);
                })
                .catch(reject);
        });
    }

    getSCIPNotificationFacts(refresh = false) {
        // if (this.scipNotificationFacts && false === refresh) {
        //     return new Promise((resolve) => resolve(this.scipNotificationFacts));
        // }
        if (refresh) {
            this.__cleanCache(['scipNotificationFacts']);
        }

        return this.__get('scipNotificationFacts', (resolve, reject) => {
            ProductApi
                .getSCIPNotificationFacts(this.id)
                .then(r => {
                    this.scipNotificationFacts = r.data;

                    resolve(this.scipNotificationFacts);
                })
                .catch(reject)
            ;
        });
    }

    getSCIPPreview() {
        return ProductApi
            .getSCIPNotificationPreview(this.id)
            .then(r => r.data);
    }

    //  Setters
    setFMDDeclarations(declarations) {
        this.fmd.data.declarations = declarations;
    }
    setLegislationQuestionAnswers(answers) {
        this.legislationQuestionAnswers.splice(0);
        answers.forEach(answer => this.legislationQuestionAnswers.push(answer));
    }
    setCard(card) {
        if (!this.inited) {
            this.inited = true;
            this.card = Vue.observable(card);
            this.isSafeUseApplicable = this.card.isSafeUseApplicable;
            this.cardPromise = new Promise((resolve, reject) => {
                resolve(this.card);
            });
        } else {
            this.card = card;
            this.isSafeUseApplicable = this.card.isSafeUseApplicable;
            this.cardPromise = new Promise((resolve, reject) => {
                resolve(this.card);
            });
        }
    }

    updateListQuestionState(list, state) {
        const currentState = this.card.substanceListQuestionStates.find(s => s.list === list.id);

        if (currentState) {
            currentState.state = state;
        } else {
            this.card.substanceListQuestionStates.push({
                list: list.id,
                state: state,
            });
        }
    }

    includeSubstance(substance, substanceListsIds = []) {
        if (this.isComplex()) {
            throw new Error('Complex product could not have any concern substances/elements');
        }

        this.__log('Include substance', substance.id);

        if (true !== this.card.hasConcernSubstances) {
            this.card.hasConcernSubstances = true;
        }

        const wrappedSubstance = this.__wrapSubstanceToContainer(substance, substanceListsIds);
        //  add to included
        let index = this.includedSubstances.findIndex(s => s.substance.id === substance.id);
        if (-1 === index) {
            this.includedSubstances.push(wrappedSubstance);
        }

        //  remove from excluded
        index = this.excludedSubstances.findIndex(s => s.substance.id === substance.id);
        if (-1 !== index) {
            this.excludedSubstances.splice(index, 1);
        }

        if (true !== this.card.informationOwnerSettings.weHaveInformation) {
            this.card.informationOwnerSettings.weHaveInformation = true;
        }

        return wrappedSubstance;
    }

    excludeSubstance(substance) {
        let index = this.includedSubstances.findIndex(s => s.substance.id === substance.id);

        if (-1 !== index) {
            this.includedSubstances.splice(index, 1);
        }

        index = this.excludedSubstances.findIndex(s => s.substance.id === substance.id);
        if (index === -1) {
            this.excludedSubstances.push({
                substance,
                status: 'excluded'
            });
        }

        if (this.includedSubstances.length === 0 && this.card.hasConcernSubstances) {
            this.card.hasConcernSubstances = null;
        }

        if (true !== this.card.informationOwnerSettings.weHaveInformation) {
            this.card.informationOwnerSettings.weHaveInformation = true;
        }
    }

    makeSubstanceUndefined(substance) {
        //  included
        let index = this.includedSubstances.findIndex(s => s.substance.id === substance.id);
        if (-1 !== index) {
            this.includedSubstances.splice(index, 1);
        }

        //  excluded
        index = this.excludedSubstances.findIndex(s => s.substance.id === substance.id);
        if (-1 !== index) {
            this.excludedSubstances.splice(index, 1);
        }

        if (false === this.card.hasConcernSubstances) {
            this.card.hasConcernSubstances = null;
        }
    }

    //  Describers
    isComplex() {
        return this.card.isComplex;
    }

    isHasConcernSubstancesSelected() {
        return this.card.hasConcernSubstances === true;
    }

    hasIncludedSubstances() {
        return this.includedSubstances.length > 0;
    }

    hasConcernElements() {
        return this.hasIncludedSubstances() || this.card.hasConcernSubstances === true;
    }

    //  Setters
    setInformationOwnerSettings(settings) {
        if (!this.card.informationOwnerSettings) {
            this.card.informationOwnerSettings = {};
        }

        this.card.informationOwnerSettings.weHaveInformation = settings.weHaveInformation;
        this.card.informationOwnerSettings.supplierHasInformation = settings.supplierHasInformation;
    }

    setLegislationSettings(key, settings) {
        // this.card.
        if (this.card.legislations) {
            const legislation = Object.values(this.card.legislations).find(l => l.key === key);

            if (legislation) {
                legislation.settings = settings;
            }
        }
    }
    setSafeUseDocument(document) {
        this.safeUseDocument.description = document.description || '';
        if (document.document) {
            this.safeUseDocument.document = document.document;
        }
    }

    setProductDescription(document) {
        this.descriptionDocument.document = document;
    }

    setComment(comment) {
        this.card.comment = comment;
    }

    setProductCharacteristics(characteristics) {
        this.card.characteristics = characteristics;
    }
    setHasConcernSubstances(hasConcernSubstances) {
        this.card.hasConcernSubstances = hasConcernSubstances;
    }

    setCategories(categories) {
        this.card.categories = categories;
    }

    setDissemblingDocument(document) {
        this.dissemblingDocument.description = document.description || '';
        if (document.document) {
            this.dissemblingDocument.document = document.document;
        }
    }

    setSuppliers(suppliers) {
        if (this.card.type === ProductType.PURCHASED || this.card.type === ProductType.PURCHASED_UNDEFINED_SUPPLIER) {
            this.card.suppliers = suppliers;
        }
    }

    //  State modifiers

    save() {
        this.__log('Saving');
        return this
            .saveCard()
            .then(() => {
                const saveFmdPromise = this.fmdInitialized ? this.saveFMD() : new Promise((resolve) => resolve());
                let saveSubstancesPromise = new Promise((resolve) => resolve());
                if (this.substancesInited) {
                    if (this.fmdInitialized) {
                        saveSubstancesPromise = saveFmdPromise
                            .then(() => this.saveExcludedSubstancesOnly());
                    } else {
                        saveSubstancesPromise = this.saveSubstances();
                    }
                }

                return Promise.all([
                    saveFmdPromise,
                    saveSubstancesPromise,
                    this.documentesInited ? this.saveVerificationDocuments() : true,
                    this.supplierVerificationDocumentsInited && this.supplierVerificationDocumentsDirty
                        ? this.saveSupplierVerificationDocuments()
                        : true,
                ]).then((r) => {
                    this.__cleanCache(['documents', 'scipNotificationFacts']);
                })
            })
            .then(() => {
                this.__log('Done');
            });
    }

    saveCard() {
        return ProductApi.update(this.id, this.__gatherProductSaveAttributes());
    }

    saveScipSettings(settings) {
        return ProductApi.updateScipSettings(this.id, settings);
    }

    saveFMD() {
        return ProductApi.saveFMD(this.id, this.fmd.version, this.fmd.data.declarations).then(r => {
            this.__cleanCache(['fmd']);
            return r.data;
        });
    }

    saveSubstances() {
        if (this.isComplex() || (this.card.includedProducts && this.card.includedProducts.length > 0)) {
            console.warn('Complex product can not have concern substances');
            return new Promise((resolve) => resolve());
        }
        return ProductApi.saveSubstances(
            this.id,
            this.__gatherIncludedSubstances(),
            this.__gatherExcludedSubstances()
        ).then(r => {
            this.__cleanCache(['concern-elements', 'substances']);
            return r.data;
        });
    }

    saveExcludedSubstancesOnly() {
        if (this.isComplex() || (this.card.includedProducts && this.card.includedProducts.length > 0)) {
            console.warn('Complex product can not have concern substances');
            return new Promise((resolve) => resolve());
        }
        return ProductApi.saveSubstances(
            this.id,
            [], // included is managed by the FMD flow
            this.__gatherExcludedSubstances(),
        ).then(r => {
            return r.data;
        });
    }

    getSubstanceState(legislations = []) {
        return ProductApi
            .getSubstanceState(this.id, legislations)
            .then(r => {
                return r.data;
            });
    }
    saveVerificationDocuments() {
        return ProductApi.saveOrganizationDocuments(
            this.id,
            this.declarationOfConformityDocuments.filter(d => {
                return d.id || d.document;
            }),
            this.evidenceReportDocuments.filter(d => {
                return d.id || d.document;
            }),
            this.compliantStatementDocuments.filter(d => {
                return d.id || d.document;
            }),
            this.safeUseDocument,
            this.dissemblingDocument,
            this.isSafeUseApplicable,
            this.descriptionDocument
        ).then(r => {
            this.__cleanCache(['documents']);
            return r;
        });
    }

    saveSupplierVerificationDocuments() {
        if (!this.supplierVerificationDocumentsDirty) {
            return Promise.resolve();
        }

        const formData = new FormData();
        const documents = this.supplierVerificationDocuments;
        const data = {
            declarationOfConformity: this.__gatherSupplierDeclarations(documents.declarationsOfConformity),
            compliantStatement: this.__gatherSupplierDeclarations(documents.compliantStatement),
            evidenceReports: this.__gatherSupplierEvidenceReports(documents.evidenceReports),
        };

        const safeUseInstructions = this.__gatherSupplierInstruction(documents.safeUseInstructions);
        const dissemblingInstructions = this.__gatherSupplierInstruction(documents.dissemblingInstructions);
        const description = this.__gatherSupplierDescription(documents.description);

        if (safeUseInstructions) {
            data.safeUseInstructions = safeUseInstructions;
        }

        if (dissemblingInstructions) {
            data.dissemblingInstructions = dissemblingInstructions;
        }

        if (description) {
            data.description = description;
        }

        ProductApi.buildFormData(formData, data);

        return ProductApi
            .saveSupplierVerificationDocuments(this.id, formData)
            .then(() => this.getSupplierVerificationDocuments(true));
    }

    remove() {
        return this
            .getCard()
            .then(() => {
                return new Promise((resolve, reject) => {
                    if (!this.card.canDelete) {
                        reject('Product could not be deleted');
                    } else {
                        ProductApi
                            .remove(this.id)
                            .then(resolve)
                            .catch(reject)
                        ;
                    }
                });
            });
    }

    makeScipSubmission() {
        return ProductApi
            .makeScipSubmission(this.id);
    }

    excludeFromScipNotification(product, parent) {
        return ProductApi.excludeFromScipNotification(this.id, product, parent);
    }

    unsetScipNotificationExclusion(product, parent) {
        return ProductApi.unsetExclusionFromScipNotification(this.id, product, parent);
    }

    changeSuppliers(suppliers) {
        const previousSuppliers = this.card.suppliers || [];
        this.card.suppliers = suppliers;
        return this.saveCard().catch((e) => {
            this.card.suppliers = previousSuppliers;
            throw e;
        });
    }

    deleteDocument(document) {
        return ProductApi
            .deleteDocument(this.id, document.id)
            .then(() => {
                let index = -1;
                switch (document.type) {
                    case VerificationDocumentType.DECLARATION_OR_CONFORMITY:
                        index = this.declarationOfConformityDocuments.findIndex(d => d.id === document.id);
                        if (index >= 0) {
                            this.declarationOfConformityDocuments.splice(index, 1);
                        }
                        break;

                    case VerificationDocumentType.EVIDENCE_REPORT:
                        index = this.evidenceReportDocuments.findIndex(d => d.id === document.id);
                        if (index >= 0) {
                            this.evidenceReportDocuments.splice(index, 1);
                        }
                        break;

                    case VerificationDocumentType.SAFE_USE_INSTRUCTIONS:
                        this.safeUseDocument.id = null;
                        this.safeUseDocument.description = null;
                        this.safeUseDocument.file = null;
                        break;

                    case VerificationDocumentType.DISSEMBLING_INSTRUCTIONS:
                        this.dissemblingDocument.id = null;
                        this.dissemblingDocument.description = null;
                        this.dissemblingDocument.file = null;
                        break;
                }
            })
        ;
    }

    includeComponent(component, numberOfUnits = 1) {
        if (!component.hasOwnProperty('numberOfUnits')) {
            component.numberOfUnits = numberOfUnits;
        }
        return ProductApi
            .includeComponents(this.id, [{id: component.id, numberOfUnits: component.numberOfUnits}])
            .then(r => {
                if (this.card.includedProducts) {
                    if (!this.card.includedProducts.find(p => p.id === component.id)) {
                        this.card.includedProducts.push(component);
                    }
                }
                this.__cleanCache(['componentsConcernElements']);
                return r;
            })
        ;
    }

    updateComponent(component) {
        return new Promise((resolve, reject) => {
            const index = this.card.includedProducts.findIndex(p => p.id === component.id);
            if (index >= 0) {
                ProductApi
                    .updateComponent(this.id, component)
                    .then(() => {
                        this.card.includedProducts[index].numberOfUnits = component.numberOfUnits || 1;
                        resolve();
                    })
                    .catch(reject)
                ;
            } else {
                resolve();
            }
        });
    }

    excludeComponent(component) {
        return ProductApi
            .excludeComponent(this.id, component.id)
            .then(r => {
                if (this.card.includedProducts) {
                    let index = this.card.includedProducts.findIndex(p => p.id === component.id);
                    if (index >= 0) {
                        this.card.includedProducts.splice(index, 1);
                    }
                }
                this.__cleanCache(['componentsConcernElements']);
                return r;
            });
    }

    convertType(type) {
        return ProductApi
            .convertType(this.id, type)
            .then(() => {
                this.card.type = type;
            });
    }

    //  Events
    hierarchyChanged() {
        this.__cleanCache(['componentsConcernElements', 'hierarchy']);
    }

    requestApproved() {
        this.__cleanCache(['componentsConcernElements']);
    }

    //  Private
    __wrapSubstanceToContainer(substance, substanceListsIds = []) {
        const container = {
            substance,
            details: {},
            properties: {},
            lists: Vue.observable([])
        };

        const substanceLists = substanceListsIds.length > 0
            ? this.substanceLists.filter(l => substanceListsIds.includes(l.id))
            : this.substanceLists;

        SubstanceApi.getSubstanceLists(substance.id)
            .then(response => {
                const substanceListIds = response.data.map(list => list.id);
                const matchingLists = substanceLists.filter(list => substanceListIds.includes(list.id));

                matchingLists.forEach(list => {
                    container.lists.push(list.toDTO());
                });
            });

        return container;
    }

    __gatherIncludedSubstances() {
        return this.includedSubstances.map(substanceContainer => {
            return {
                id: substanceContainer.substance.id,
                properties: substanceContainer.properties
            }
        });
    }

    __gatherExcludedSubstances() {
        return this.excludedSubstances.map(substance => {
            return substance instanceof Object ? substance.substance.id : substance;
        });
    }

    __gatherSupplierVerificationDocumentsState() {
        return {
            dirty: this.supplierVerificationDocumentsDirty,
        };
    }

    __replaceSupplierVerificationDocuments(documents) {
        Object.keys(documents).forEach(key => {
            Vue.set(this.supplierVerificationDocuments, key, documents[key]);
        });
    }

    __replaceSupplierVerificationReplyDocuments(documents) {
        Object.keys(documents).forEach(key => {
            Vue.set(this.supplierVerificationReplyDocuments, key, documents[key]);
        });
    }

    __gatherSupplierDeclarations(documents) {
        return (documents || [])
            .filter(document => document.id || document.document instanceof File)
            .map(document => {
                const data = {
                    legislations: this.__gatherIdList(document.legislations),
                    declarationDate: document.declarationDate,
                    declarationValidityDate: document.declarationValidityDate,
                };

                if (document.id) {
                    data.id = document.id;
                }

                if (document.document instanceof File) {
                    data.document = document.document;
                }

                return data;
            });
    }

    __gatherSupplierEvidenceReports(documents) {
        return (documents || [])
            .filter(document => document.id || document.document instanceof File)
            .map(document => {
                const data = {
                    legislations: this.__gatherIdList(document.legislations),
                    documentTypes: this.__gatherIdList(document.documentTypes),
                };

                if (document.id) {
                    data.id = document.id;
                }

                if (document.document instanceof File) {
                    data.document = document.document;
                }

                return data;
            });
    }

    __gatherSupplierInstruction(document) {
        if (!document) {
            return null;
        }

        const data = {
            description: document.description || '',
        };

        if (document.id) {
            data.id = document.id;
        }

        if (document.document instanceof File) {
            data.document = document.document;
        }

        return data.id || data.description || data.document ? data : null;
    }

    __gatherSupplierDescription(document) {
        if (!document) {
            return null;
        }

        const data = {};

        if (document.id) {
            data.id = document.id;
        }

        if (document.document instanceof File) {
            data.document = document.document;
        }

        return data.id || data.document ? data : null;
    }

    __normalizeSupplierVerificationDocuments(documents) {
        const items = Array.isArray(documents) ? documents : [];
        const verificationDocuments = items.filter(document => document.documentType === DocumentType.VERIFICATION);

        return {
            declarationsOfConformity: verificationDocuments.filter(document => {
                return document.verificationType === VerificationDocumentType.DECLARATION_OR_CONFORMITY;
            }).map(document => this.__normalizeSupplierVerificationDocument(document)),
            compliantStatement: verificationDocuments.filter(document => {
                return document.verificationType === VerificationDocumentType.COMPLIANT_STATEMENT;
            }).map(document => this.__normalizeSupplierVerificationDocument(document)),
            evidenceReports: verificationDocuments.filter(document => {
                return document.verificationType === VerificationDocumentType.EVIDENCE_REPORT;
            }).map(document => this.__normalizeSupplierVerificationDocument(document)),
            other: verificationDocuments.filter(document => {
                return document.verificationType === VerificationDocumentType.OTHER;
            }).map(document => this.__normalizeSupplierVerificationDocument(document)),
            safeUseInstructions: this.__normalizeSupplierInstruction(verificationDocuments.find(document => {
                return document.verificationType === VerificationDocumentType.SAFE_USE_INSTRUCTIONS;
            })),
            dissemblingInstructions: this.__normalizeSupplierInstruction(verificationDocuments.find(document => {
                return document.verificationType === VerificationDocumentType.DISSEMBLING_INSTRUCTIONS;
            })),
            description: items.find(document => document.documentType === DocumentType.DESCRIPTION) || null,
            unclassifiedVerification: items.filter(document => {
                return document.documentType === DocumentType.UNCLASSIFIED_VERIFICATION;
            }),
        };
    }

    __normalizeSupplierVerificationDocument(document) {
        return {
            ...document,
            legislations: Array.isArray(document.legislations) ? document.legislations : [],
            documentTypes: Array.isArray(document.documentTypes) ? document.documentTypes : [],
            file: document.file || {name: ''},
        };
    }

    __normalizeSupplierInstruction(document) {
        return {
            ...(document || {}),
            description: document && document.description ? document.description : '',
            file: document && document.file ? document.file : null,
        };
    }

    __normalizeSupplierVerificationReplyDocuments(documents) {
        const reply = documents || {};

        return {
            requestId: reply.requestId || null,
            declarationsOfConformity: Array.isArray(reply.declarationsOfConformity) ? reply.declarationsOfConformity : [],
            compliantStatement: Array.isArray(reply.compliantStatement) ? reply.compliantStatement : [],
            evidenceReports: Array.isArray(reply.evidenceReports) ? reply.evidenceReports : [],
            safeUseInstruction: reply.safeUseInstruction || null,
            dissemblingInstruction: reply.dissemblingInstruction || null,
        };
    }

    __makeEmptySupplierVerificationReplyDocuments() {
        return {
            requestId: null,
            declarationsOfConformity: [],
            compliantStatement: [],
            evidenceReports: [],
            safeUseInstruction: null,
            dissemblingInstruction: null,
        };
    }

    __gatherProductSaveAttributes() {
        let legislationSettings = {};
        Object.keys(this.card.legislations).forEach(index => {
            if (this.card.legislations[index].key !== 'generic') {
                legislationSettings[this.card.legislations[index].key] = this.card.legislations[index].settings;
            }
        });
        return {
            identifiers: this.card.identifiers.filter(v => v.key && v.value),
            names: this.card.names.filter(v => v.key && v.value),
            businessRoles: this.card.businessRoles.map(r => r.id),
            comment: this.card.comment || '',
            characteristics: this.card.characteristics,
            includedProducts: this.card.includedProducts.map(p => {
                return {
                    id: p.id,
                    numberOfUnits: p.numberOfUnits,
                };
            }),
            parentGroups: this.card.productGroups.map(p => p.id),
            categories: this.card.categories.map(c => c.id),
            materialCategoryIds: this.__gatherIdList(this.card.materialCategoryIds),
            mixtureCategoryIds: this.__gatherIdList(this.card.mixtureCategoryIds),
            suppliers: this.card.suppliers.map(s => s.id),
            hasConcernSubstances: this.card.hasConcernSubstances,
            countryOfOrigin: this.card.countryOfOrigin ? this.card.countryOfOrigin.id : null,
            informationOwnerSettings: this.card.informationOwnerSettings,
            scipSettings: this.card.scipSettings,
            legislationSettings: legislationSettings,
            substanceListQuestionStates: this.card.substanceListQuestionStates,
            regulatedProductStatements: this.card.regulatedProductStatements,
            legislationQuestionAnswers: this.legislationQuestionAnswers || [],
        };
    }

    __gatherIdList(value) {
        if (!(value instanceof Array)) {
            return [];
        }

        return value
            .map(item => item instanceof Object ? item.id : item)
            .filter(id => id !== null && id !== undefined && id !== '');
    }

    __get(name, requestInitiator, cleanCallback) {
        if (this.data[name]) {
            return this.data[name].promise;
        }

        this.data[name] = {
            promise: new Promise(requestInitiator),
            cleanCallback
        };

        setTimeout(() => {
            this.__cleanCache([name]);
        }, 60000);

        return this.data[name].promise;
    }

    __cleanCache(names) {
        this.__log('Clearing caches for: ' + names.join(', '));
        names.forEach(name => {
            if (this.data[name]) {
                if (this.data[name].cleanCallback) {
                    this.__log('Run clean callback for ' + name);
                    this.data[name].cleanCallback();
                }
                delete this.data[name];
            }
        })
    }

    __log() {
        console.log('[Product][' + this.id + ']', ...arguments);
    }
}
